import { useState, useMemo } from 'react';
import { Button, Collapse, Box, TablePagination } from '@mui/material';
import PropTypes from 'prop-types';
import icon from './../../../assets/images/no-image.png';
import EditIcon from '@mui/icons-material/Edit';
import SignChangeComponent from './SignChangeComponent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {
  StyledTableCell,
  StyledTableRow,
  StyledTableContainer,
  PrimaryButton,
} from '../../../utils/common/styles/commonStyles';
import { styled, useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows.heavy,
}));

const TableComponent = ({
  markers = [],
  onMarkerChange,
  deleteSign,
  openSignGrid,
  setOpenSignGrid,
  filteredImages,
  handleSearch,
  searchTerm,
  handleCancelSearch,
  selectedSign,
  setSelectedSign,
  replaceSign,
}) => {
  const theme = useTheme();

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [anchorElName, setAnchorElName] = useState(null);
  const [anchorElCode, setAnchorElCode] = useState(null);
  const openName = Boolean(anchorElName);
  const openCode = Boolean(anchorElCode);

  const handleChangeSigntable = (row) => {
    // Toggle the sign change component for this row
    if (selectedRowId === row.id && openSignGrid) {
      setOpenSignGrid(false);
      setSelectedRowId(null);
    } else {
      setSelectedRowId(row.id);
      setOpenSignGrid(true);
      onMarkerChange(row.originalData);
    }
  };

  const rows = useMemo(() => {
    const nameSearch = filterName.trim().toLowerCase();
    const codeSearch = filterCode.trim().toLowerCase();

    let filteredMarkers = markers;

    if (nameSearch) {
      filteredMarkers = filteredMarkers.filter((item) =>
        (item.name ?? '').toLowerCase().includes(nameSearch)
      );
    }

    if (codeSearch) {
      filteredMarkers = filteredMarkers.filter((item) =>
        (item.code ?? '').toLowerCase().includes(codeSearch)
      );
    }

    return filteredMarkers.map((item) => ({
      id: item.id,
      name: item.name || 'No marker selected',
      type: item.type,
      signCode: item.code,
      signImage: item.icon || icon,
      originalData: item,
    }));
  }, [markers, filterName, filterCode]);

  const paginatedRows = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return rows.slice(startIndex, startIndex + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSelectedRowId(null);
    setOpenSignGrid(false);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSelectedRowId(null);
    setOpenSignGrid(false);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = paginatedRows.map((row) => row.id);
      setSelectedRows(newSelected);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedRows, id];
    } else {
      newSelected = selectedRows.filter((rowId) => rowId !== id);
    }

    setSelectedRows(newSelected);
  };

  const isSelected = (id) => selectedRows.indexOf(id) !== -1;

  const handleDeleteSelected = () => {
    // Call deleteSign for each selected row
    deleteSign(selectedRows);
    // Clear selection after deletion
    setSelectedRows([]);
  };

  const handleSearchClick = (event, field) => {
    if (field === 'name') setAnchorElName(event.currentTarget);
    else setAnchorElCode(event.currentTarget);
  };

  const handleSearchClose = () => {
    setAnchorElName(null);
    setAnchorElCode(null);
    setFilterCode('');
    setFilterName('');
  };

  const columns = [
    { label: 'Name', dataKey: 'name', width: '30%' },
    { label: 'Type', dataKey: 'type', width: '20%' },
    { label: 'Sign Code', dataKey: 'signCode', width: '20%' },
    { label: 'Sign Image', dataKey: 'signImage', width: '15%' },
    { label: 'Actions', dataKey: 'actions', width: '5%' },
  ];

  return (
    <Grid
      item
      xs={12}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '82vh',
      }}
    >
      <StyledCard>
        <CardContent
          sx={{ '&.MuiCardContent-root': { padding: '16px 16px 8px 16px', overflowY: 'auto' } }}
        >
          <StyledTableContainer sx={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader aria-label="signs table" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={
                        paginatedRows.length > 0 && selectedRows.length === paginatedRows.length
                      }
                      onChange={handleSelectAllClick}
                      aria-label="Select all rows"
                    />
                  </StyledTableCell>
                  {columns.map((column) => (
                    <StyledTableCell
                      key={column.dataKey}
                      align="left"
                      style={{ width: column.width }}
                    >
                      {column.dataKey === 'name' ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {column.label}
                          <IconButton
                            size="small"
                            onClick={(e) => handleSearchClick(e, 'name')}
                            sx={{ color: 'primary.alt', padding: 0.5, opacity: 0.8 }}
                          >
                            <FilterAltOutlinedIcon fontSize="small" />
                          </IconButton>

                          <Popover
                            open={openName}
                            anchorEl={anchorElName}
                            onClose={handleSearchClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            sx={{
                              '& .MuiPaper-root.MuiPopover-paper': {
                                backgroundColor: 'background.light',
                                boxShadow: theme.shadows.heavy,
                              },
                            }}
                          >
                            <div
                              style={{
                                padding: 16,
                                minWidth: 300,
                                backgroundColor: '#fff',
                                boxShadow: '0px 3px 6px rgba(0,0,0,0.16)',
                                borderRadius: 4,
                              }}
                            >
                              <TextField
                                id="filterName"
                                autoComplete="off"
                                name="filter by name"
                                fullWidth
                                label="Search"
                                variant="outlined"
                                size="small"
                                value={filterName}
                                onChange={(e) => setFilterName(e.target.value)}
                              />
                            </div>
                          </Popover>
                        </Box>
                      ) : column.dataKey === 'signCode' ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {column.label}
                          <IconButton
                            size="small"
                            onClick={(e) => handleSearchClick(e, 'code')}
                            sx={{ color: 'primary.alt', padding: 0.5, opacity: 0.8 }}
                          >
                            <FilterAltOutlinedIcon fontSize="small" />
                          </IconButton>

                          <Popover
                            open={openCode}
                            anchorEl={anchorElCode}
                            onClose={handleSearchClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            sx={{
                              '& .MuiPaper-root.MuiPopover-paper': {
                                backgroundColor: 'background.light',
                                boxShadow: theme.shadows.heavy,
                              },
                            }}
                          >
                            <div
                              style={{
                                padding: 16,
                                minWidth: 300,
                                backgroundColor: '#fff',
                                boxShadow: '0px 3px 6px rgba(0,0,0,0.16)',
                                borderRadius: 4,
                              }}
                            >
                              <TextField
                                id="filterCode"
                                autoComplete="off"
                                name="filter by code"
                                fullWidth
                                label="Search"
                                variant="outlined"
                                size="small"
                                value={filterCode}
                                onChange={(e) => setFilterCode(e.target.value)}
                              />
                            </div>
                          </Popover>
                        </Box>
                      ) : (
                        column.label
                      )}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => {
                  const isItemSelected = isSelected(row.id);
                  return (
                    <>
                      <StyledTableRow key={row.id} selected={isItemSelected}>
                        <StyledTableCell padding="checkbox">
                          <input
                            type="checkbox"
                            checked={isItemSelected}
                            onChange={() => handleSelectRow(row.id)}
                            style={{ accentColor: '#00B09B' }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <span style={{ color: '#2c3e50', fontWeight: '500' }}>{row.name}</span>
                        </StyledTableCell>
                        <StyledTableCell align="left">{row.type}</StyledTableCell>
                        <StyledTableCell align="left">
                          <span style={{ fontFamily: 'monospace', color: '#e74c3c' }}>
                            {row.signCode}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <img
                            src={row.signImage}
                            alt="Sign"
                            style={{
                              maxHeight: '40px',
                              maxWidth: '60px',
                              objectFit: 'contain',
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Button
                            onClick={() => {
                              if (selectedRows.length <= 1) handleChangeSigntable(row);
                            }}
                            disabled={selectedRows.length > 1}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              minWidth: 'auto',
                              cursor: selectedRows.length > 1 ? 'not-allowed' : 'pointer',
                              color:
                                selectedRows.length > 1
                                  ? '#b6b6b6'
                                  : selectedRowId === row.id && openSignGrid
                                    ? '#1976d2'
                                    : '#00B09B',
                              opacity: selectedRows.length > 1 ? 0.5 : 1,
                            }}
                          >
                            <EditIcon />
                          </Button>
                        </StyledTableCell>
                      </StyledTableRow>
                      {/* Inline Sign Change Component */}
                      {selectedRowId === row.id && (
                        <TableRow>
                          <StyledTableCell colSpan={6} style={{ padding: 0, border: 'none' }}>
                            <Collapse in={openSignGrid} timeout={300}>
                              <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                                <SignChangeComponent
                                  open={openSignGrid}
                                  setOpen={setOpenSignGrid}
                                  images={filteredImages}
                                  handleSearch={handleSearch}
                                  searchTerm={searchTerm}
                                  handleCancelSearch={handleCancelSearch}
                                  selectedSign={selectedSign}
                                  setSelectedSign={setSelectedSign}
                                  replaceSign={replaceSign}
                                />
                              </Box>
                            </Collapse>
                          </StyledTableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
            {/* Pagination and Actions */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #e0e0e0',
                backgroundColor: '#f8f8f8',
                px: 2,
                py: 1,
                borderRadius: 1,
              }}
            >
              <PrimaryButton
                type="button"
                onClick={handleDeleteSelected}
                disabled={selectedRows.length === 0}
                variant="contained"
              >
                Delete ({selectedRows.length}) Signs
              </PrimaryButton>

              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    minHeight: '48px',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    margin: 0,
                  },
                }}
              />
            </Box>
          </StyledTableContainer>
        </CardContent>
      </StyledCard>
    </Grid>
  );
};

export default TableComponent;

TableComponent.propTypes = {
  markers: PropTypes.array.isRequired,
  onMarkerChange: PropTypes.func.isRequired,
  deleteSign: PropTypes.func.isRequired,
  openSignGrid: PropTypes.bool.isRequired,
  setOpenSignGrid: PropTypes.func.isRequired,
  filteredImages: PropTypes.array.isRequired,
  handleSearch: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  handleCancelSearch: PropTypes.func.isRequired,
  selectedSign: PropTypes.object,
  setSelectedSign: PropTypes.func.isRequired,
  replaceSign: PropTypes.func.isRequired,
};
